import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDesignStore, useProductSelectionStore } from '@/stores';
import { useDesignPricing, usePositionPreviews, useProducts } from '@/hooks';
import { useAdaptedPreviews } from '@/hooks/useAdaptedPreviews';
import {
  transformProduct,
  findTemplateByPosition,
  getDefaultTemplate,
  type UIProductMockupTemplate,
} from '@/lib/product-transformer';
import MockupRenderer from '@/components/editor/MockupRenderer';
import {
  getEffectiveCropSrc,
  useCanvasStateManager,
} from '@/stores/canvasStateManager';
import { cn } from '@/lib/utils';
import { isEmbroideryProduct } from '@/lib/decoration-method';
import { formatMoney } from '@/lib/format-money';
import { netOf, round2 } from '@/lib/pricing-math';
import type { AdaptedPlacement } from '@designdeck/shared';

const SHIRT_ICON_COLORS = ['#D6D6DE', '#C2C2CC', '#ADADBA', '#9B9BAA'] as const;

function getShirtIconColor(index: number): string {
  return SHIRT_ICON_COLORS[index % SHIRT_ICON_COLORS.length] ?? '#D6D6DE';
}

function getDecorationMethodLabel(
  positions: Array<{
    slug?: string;
    decorationMethod?: {
      name?: string;
      description?: string;
    } | null;
    decorationMethods?: {
      name?: string;
      description?: string;
    } | null;
  }>
): string {
  const front = positions.find((pos) => pos.slug === 'front') ?? positions[0];
  const method = front?.decorationMethods ?? front?.decorationMethod;
  if (!method) return '';
  return method.name || method.description || '';
}

function ProductShirtIcon({
  color,
  className,
}: {
  color: string;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 100 92"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M31 6 L15 14 5 32 19 40 23 34 23 86 77 86 77 34 81 40 95 32 85 14 69 6 C65 14 58 18 50 18 C42 18 35 14 31 6 Z"
        fill={color}
      />
    </svg>
  );
}

interface CenteredPreviewMockupProps {
  productId: string;
  template: UIProductMockupTemplate;
  backgroundColor: string;
  designDataUrl: string | null;
  adaptedPlacement: AdaptedPlacement | null;
  designCropUrl: string | null;
  isHeathered: boolean;
  /** Embroidery keeps the garment shading below the design */
  isEmbroidery?: boolean;
  /** Share of the container the mockup may fill; the hero leaves a margin. */
  fit?: number;
}

/**
 * Measures its container and centers the mockup with correct aspect ratio.
 */
function CenteredPreviewMockup({
  productId,
  template,
  backgroundColor,
  designDataUrl,
  adaptedPlacement,
  designCropUrl,
  isHeathered,
  isEmbroidery = false,
  fit = 0.86,
}: CenteredPreviewMockupProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState({ width: 320, height: 320 });

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setMeasured({ width: rect.width, height: rect.height });
      }
    };

    measure();

    const observer = new ResizeObserver(() => measure());
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const mockupWidth = template.mockupWidthPx || 500;
  const mockupHeight = template.mockupHeightPx || 500;
  const scaleX = measured.width / mockupWidth;
  const scaleY = measured.height / mockupHeight;
  const fitScale = Math.min(scaleX, scaleY) * fit;
  const dimensions = {
    width: Math.round(mockupWidth * fitScale),
    height: Math.round(mockupHeight * fitScale),
  };

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 flex items-center justify-center"
    >
      <MockupRenderer
        productId={productId}
        template={template}
        backgroundColor={backgroundColor}
        designDataUrl={designDataUrl}
        adaptedPlacement={adaptedPlacement}
        designCropUrl={designCropUrl}
        isHeathered={isHeathered}
        isEmbroidery={isEmbroidery}
        width={dimensions.width}
        height={dimensions.height}
        lazyLoad={false}
        priority="high"
      />
    </div>
  );
}

/**
 * Preview + summary card for Publish step.
 */
export function PreviewSummary() {
  const { t, i18n } = useTranslation();
  const [isAdditionalExpanded, setIsAdditionalExpanded] = useState(false);
  // Selling prices are EUR throughout the creator; only the notation
  // follows the shop language.
  const formatCurrency = (value: number) =>
    formatMoney(value, 'EUR', i18n.language);

  const { data: apiProducts } = useProducts();
  const selections = useProductSelectionStore((state) => state.selections);
  const designId = useDesignStore((state) => state.designId);
  // Seller cost per product from the quote; the profit shown is the live
  // price, net of VAT, minus that net cost, so an unsaved price edit reads
  // correctly too. A quote without a VAT rate (an older API build during a
  // deploy window) converts with the fixed 19 %.
  const { data: pricing } = useDesignPricing(designId);
  const profitOf = (product: {
    id: string;
    sellingPrice?: number;
  }): number | null => {
    const entry = pricing?.products.find((q) => q.productId === product.id);
    const cost = entry?.sellerCost != null ? Number(entry.sellerCost) : null;
    const net =
      typeof product.sellingPrice === 'number'
        ? netOf(product.sellingPrice, entry?.vatRate)
        : null;
    return net !== null && cost !== null ? round2(net - cost) : null;
  };
  const focusedProductId = useProductSelectionStore(
    (state) => state.focusedProductId
  );
  const focusedColorId = useProductSelectionStore(
    (state) => state.focusedColorId
  );

  const { previews: positionPreviews } = usePositionPreviews();
  const frontPreviewUrl = positionPreviews?.front ?? null;

  const products = useMemo(() => {
    if (!apiProducts) return [];
    return apiProducts.map((apiProduct) => {
      const selection = selections[apiProduct.id];
      const uiProduct = transformProduct(
        apiProduct,
        selection?.selectedColors || [],
        selection?.isActive || false
      );
      // The seller's price only; the catalogue recommendation is no price.
      return { ...uiProduct, sellingPrice: selection?.sellingPrice };
    });
  }, [apiProducts, selections]);

  const { adaptations } = useAdaptedPreviews(products);

  const mainProduct = useMemo(() => {
    const activeProducts = products.filter((product) => product.isActive);
    if (!activeProducts.length) return null;
    if (focusedProductId) {
      const focusedActive = activeProducts.find(
        (product) => product.id === focusedProductId
      );
      if (focusedActive) return focusedActive;
    }
    return activeProducts[0] ?? null;
  }, [products, focusedProductId]);

  const additionalActiveProducts = useMemo(
    () =>
      products.filter(
        (product) => product.isActive && product.id !== mainProduct?.id
      ),
    [products, mainProduct?.id]
  );

  const mainColor = useMemo(() => {
    if (!mainProduct) return null;
    if (focusedColorId && mainProduct.id === focusedProductId) {
      return mainProduct.colors.find((color) => color.id === focusedColorId);
    }
    const selectedId = mainProduct.selectedColors[0];
    return (
      mainProduct.colors.find((color) => color.id === selectedId) ??
      mainProduct.colors[0] ??
      null
    );
  }, [mainProduct, focusedColorId, focusedProductId]);

  const frontTemplate = useMemo(() => {
    if (!mainProduct) return null;
    return (
      findTemplateByPosition(mainProduct.mockupTemplates, 'front') ||
      getDefaultTemplate(mainProduct.mockupTemplates)
    );
  }, [mainProduct]);

  const frontAdaptation = useMemo(
    () => adaptations?.get(mainProduct?.id ?? '')?.get('front') ?? null,
    [adaptations, mainProduct?.id]
  );

  const masterFrontCrop = useCanvasStateManager(
    (state) => state.masterPositions.front
  );
  const designCropUrl =
    frontAdaptation?.cropUrl ??
    (masterFrontCrop ? getEffectiveCropSrc(masterFrontCrop) : null);

  const sellingPrice = mainProduct?.sellingPrice;
  const profit = mainProduct ? profitOf(mainProduct) : null;
  const printMethod = mainProduct
    ? getDecorationMethodLabel(mainProduct.positions)
    : '';

  return (
    <aside className="bg-[var(--paper-2)] rounded-card border border-[var(--line)] overflow-hidden">
      <div className="p-5">
        <p className="dc-type-section text-[var(--mute)] mb-3.5">
          {t('creator.publish.summaryTitle')}
        </p>

        {mainProduct ? (
          <>
            <div className="relative rounded-control bg-[var(--paper)] h-80 overflow-hidden">
              {mainProduct.isPremium && (
                <span
                  data-tone="success"
                  className="dc-media-badge dc-type-chip absolute top-3 right-3 z-10 px-2 py-0.5 rounded-chip bg-[var(--paper)] ring-1 ring-inset ring-[var(--line)] text-[var(--success)]"
                >
                  {t('creator.publish.organicBadge')}
                </span>
              )}

              {frontTemplate ? (
                <CenteredPreviewMockup
                  productId={mainProduct.id}
                  template={frontTemplate}
                  backgroundColor={mainColor?.hex ?? '#FFFFFF'}
                  designDataUrl={frontPreviewUrl}
                  adaptedPlacement={frontAdaptation?.placement ?? null}
                  designCropUrl={designCropUrl}
                  isHeathered={mainColor?.isHeathered ?? false}
                  isEmbroidery={isEmbroideryProduct(mainProduct)}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-[var(--mute)] text-sm">
                  {t('creator.publish.mockupPreview')}
                </div>
              )}
            </div>

            <div className="mt-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="dc-type-card-title text-[var(--ink)] leading-snug">
                  {mainProduct.name}
                </h3>
                {printMethod && (
                  <p className="text-sm text-[var(--mute)] mt-0.5">
                    {printMethod}
                  </p>
                )}
              </div>

              <div className="text-right shrink-0">
                <p className="dc-type-price text-[var(--ink)]">
                  {typeof sellingPrice === 'number'
                    ? formatCurrency(sellingPrice)
                    : '—'}
                </p>
                <p
                  className={cn(
                    'dc-type-data mt-0.5',
                    profit === null || profit >= 0
                      ? 'text-[var(--success)]'
                      : 'text-[var(--danger)]'
                  )}
                >
                  {profit === null
                    ? t('creator.publish.profitUnknown')
                    : t('creator.publish.profit', {
                        amount: formatCurrency(profit),
                      })}
                </p>
              </div>
            </div>

            {additionalActiveProducts.length > 0 && (
              <>
                <div className="h-px bg-[var(--line)] my-4" />

                <button
                  type="button"
                  onClick={() => setIsAdditionalExpanded((open) => !open)}
                  aria-expanded={isAdditionalExpanded}
                  className="w-full flex items-center justify-between rounded-control px-0.5 py-1 hover:bg-[var(--paper-3)] transition-colors"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="flex items-center">
                      {additionalActiveProducts
                        .slice(0, 4)
                        .map((product, index) => (
                          <div
                            key={product.id}
                            className={cn(
                              'w-8 h-8 rounded-full bg-[var(--paper)] border border-[var(--line)] flex items-center justify-center overflow-hidden',
                              index > 0 && '-ml-2'
                            )}
                            title={product.name}
                          >
                            {product.thumbnailUrl || product.mockupImageUrl ? (
                              <img
                                src={
                                  product.thumbnailUrl || product.mockupImageUrl
                                }
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <ProductShirtIcon
                                color={getShirtIconColor(index)}
                                className="w-4 h-4"
                              />
                            )}
                          </div>
                        ))}
                    </div>
                    <span className="text-sm font-semibold text-[var(--ink-2)] truncate">
                      {t('creator.publish.moreProducts', {
                        count: additionalActiveProducts.length,
                      })}
                    </span>
                  </div>

                  <ChevronDown
                    className={cn(
                      'w-4 h-4 text-[var(--mute)] shrink-0 transition-transform',
                      isAdditionalExpanded && 'rotate-180'
                    )}
                  />
                </button>

                {isAdditionalExpanded && (
                  <div className="mt-2.5 pt-3 border-t border-[var(--line)] space-y-1">
                    <div className="flex items-center gap-3 px-0.5 pb-1">
                      <span className="w-5 shrink-0" aria-hidden="true" />
                      <span className="flex-1" aria-hidden="true" />
                      <span className="dc-type-label w-[4.75rem] text-right text-[var(--mute)] shrink-0">
                        {t('creator.publish.sellingPriceColumn')}
                      </span>
                      <span className="dc-type-label w-[4.75rem] text-right text-[var(--mute)] shrink-0">
                        {t('creator.publish.profitColumn')}
                      </span>
                    </div>
                    {additionalActiveProducts.map((product, index) => {
                      const productProfit = profitOf(product);
                      // The same finished front the products step shows on
                      // its card: adapted placement per product, crop from
                      // the master. Falls back to the grey shirt when the
                      // product has no front template.
                      const rowTemplate =
                        findTemplateByPosition(
                          product.mockupTemplates,
                          'front'
                        ) || getDefaultTemplate(product.mockupTemplates);
                      const rowAdaptation =
                        adaptations?.get(product.id)?.get('front') ?? null;
                      const rowColorId = product.selectedColors[0];
                      const rowColor =
                        product.colors.find(
                          (color) => color.id === rowColorId
                        ) ??
                        product.colors[0] ??
                        null;

                      return (
                        <div
                          key={product.id}
                          className="flex items-center gap-3 py-1.5 px-0.5"
                        >
                          {rowTemplate ? (
                            <div
                              className="relative w-10 h-10 shrink-0 rounded-chip bg-[var(--paper)] overflow-hidden"
                              aria-hidden="true"
                            >
                              <CenteredPreviewMockup
                                productId={product.id}
                                template={rowTemplate}
                                backgroundColor={rowColor?.hex ?? '#FFFFFF'}
                                designDataUrl={frontPreviewUrl}
                                adaptedPlacement={
                                  rowAdaptation?.placement ?? null
                                }
                                designCropUrl={
                                  rowAdaptation?.cropUrl ?? designCropUrl
                                }
                                isHeathered={rowColor?.isHeathered ?? false}
                                isEmbroidery={isEmbroideryProduct(product)}
                                fit={1}
                              />
                            </div>
                          ) : (
                            <ProductShirtIcon
                              color={getShirtIconColor(index)}
                              className="w-5 h-5 shrink-0"
                            />
                          )}
                          <span className="text-sm font-semibold text-[var(--ink-2)] flex-1 truncate">
                            {product.name}
                          </span>
                          <span className="dc-type-data w-[4.75rem] text-right text-[var(--mute)] shrink-0 tabular-nums">
                            {typeof product.sellingPrice === 'number'
                              ? formatCurrency(product.sellingPrice)
                              : '—'}
                          </span>
                          <span
                            className={cn(
                              'dc-type-data w-[4.75rem] text-right shrink-0 tabular-nums',
                              productProfit === null || productProfit >= 0
                                ? 'text-[var(--success)]'
                                : 'text-[var(--danger)]'
                            )}
                          >
                            {productProfit === null
                              ? '—'
                              : formatCurrency(productProfit)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <div className="rounded-control bg-[var(--paper)] h-80 flex items-center justify-center text-[var(--mute)] text-sm">
            {t('creator.publish.noActiveProduct')}
          </div>
        )}
      </div>
    </aside>
  );
}
